/**
 * Keyword Strategy Service
 * AI-powered match type recommendations and keyword scoring
 */

import { KeywordIdea, LandingPageAnalysis } from '../types/googleAds';
import { openaiService } from './openaiService';
import { ragService } from './ragService';
import { ragKnowledgeService } from './ragKnowledgeService';
import { getClient } from './geminiService';

export const keywordStrategyService = {
  /**
   * Detect if keyword is branded
   */
  detectBrandKeyword(
    keywordText: string, 
    analysis: LandingPageAnalysis
  ): { isBranded: boolean; matchType: 'exact' | 'partial' | 'none'; confidence: number } {
    const keyword = keywordText.toLowerCase().trim();
    
    // Get brand indicators from analysis
    const businessName = (analysis.businessName || analysis.title || '').toLowerCase();
    const brandWords = businessName.split(/[\s\-_]+/).filter(w => w.length > 2);
    
    // Exact brand match (e.g., "vobahome", "volksbank")
    if (brandWords.some(brand => keyword === brand)) {
      return { isBranded: true, matchType: 'exact', confidence: 100 };
    }
    
    // Brand + qualifier (e.g., "vobahome teilverkauf", "volksbank immobilien")
    if (brandWords.some(brand => keyword.includes(brand) && keyword.length > brand.length)) {
      return { isBranded: true, matchType: 'partial', confidence: 95 };
    }
    
    // Check if keyword starts with brand
    if (brandWords.some(brand => keyword.startsWith(brand))) {
      return { isBranded: true, matchType: 'partial', confidence: 90 };
    }
    
    // Not branded
    return { isBranded: false, matchType: 'none', confidence: 0 };
  },

  /**
   * Recommend match type based on keyword characteristics
   */
  recommendMatchType(
    keyword: KeywordIdea,
    analysis: LandingPageAnalysis
  ): {
    matchType: 'EXACT' | 'PHRASE' | 'BROAD';
    reason: string;
    confidence: number;
  } {
    let matchType: 'EXACT' | 'PHRASE' | 'BROAD' = 'PHRASE';
    let reason = '';
    let confidence = 70;
    
    // Rule 1: High Intent Keywords → EXACT
    const highIntentWords = ['buy', 'price', 'cost', 'purchase', 'shop', 'order', 'pricing', 'quote'];
    if (highIntentWords.some(word => keyword.text.toLowerCase().includes(word))) {
      matchType = 'EXACT';
      reason = 'High purchase intent - exact match for precise targeting';
      confidence = 90;
      return { matchType, reason, confidence };
    }
    
    // Rule 2: Brand Keywords → EXACT
    const brandName = analysis.title.toLowerCase().split(' ')[0];
    if (keyword.text.toLowerCase().includes(brandName) && brandName.length > 3) {
      matchType = 'EXACT';
      reason = 'Brand keyword - exact match to protect brand';
      confidence = 95;
      return { matchType, reason, confidence };
    }
    
    // Rule 3: High Competition + High CPC → EXACT (Budget Control)
    if (keyword.competition === 'HIGH' && keyword.competitionIndex > 75 && keyword.suggestedBid > 5) {
      matchType = 'EXACT';
      reason = 'High competition & CPC - exact match for budget control';
      confidence = 85;
      return { matchType, reason, confidence };
    }
    
    // Rule 4: Low Search Volume → BROAD (Discovery)
    if (keyword.monthlySearches < 500) {
      matchType = 'BROAD';
      reason = 'Low search volume - broad match for discovery';
      confidence = 75;
      return { matchType, reason, confidence };
    }
    
    // Rule 5: Very Low Competition → BROAD (Easy wins)
    if (keyword.competition === 'LOW' && keyword.competitionIndex < 30) {
      matchType = 'BROAD';
      reason = 'Low competition - broad match for maximum reach';
      confidence = 80;
      return { matchType, reason, confidence };
    }
    
    // Rule 6: Long-tail Keywords (4+ words) → PHRASE
    const wordCount = keyword.text.split(' ').length;
    if (wordCount >= 4) {
      matchType = 'PHRASE';
      reason = 'Long-tail keyword - phrase match optimal';
      confidence = 85;
      return { matchType, reason, confidence };
    }
    
    // Rule 7: Question Keywords → PHRASE
    const questionWords = ['how', 'what', 'where', 'why', 'when', 'which'];
    if (questionWords.some(word => keyword.text.toLowerCase().startsWith(word))) {
      matchType = 'PHRASE';
      reason = 'Question keyword - phrase match for context';
      confidence = 80;
      return { matchType, reason, confidence };
    }
    
    // Default: Balanced Keywords → PHRASE
    reason = 'Balanced keyword - phrase match recommended';
    confidence = 70;
    return { matchType, reason, confidence };
  },
  
  /**
   * Calculate relevance score for keyword vs landing page
   * Uses AI to score 0-100
   */
  async calculateRelevanceScore(
    keyword: KeywordIdea,
    analysis: LandingPageAnalysis
  ): Promise<number> {
    try {
      const prompt = `Landing Page Value Proposition: "${analysis.valueProposition}"
Industry: ${analysis.industry}
Target Audience: ${analysis.targetAudience}
Key Benefits: ${analysis.keyBenefits.join(', ')}
Suggested Keywords: ${analysis.suggestedKeywords.join(', ')}

Keyword to score: "${keyword.text}"

Score this keyword's relevance to the landing page on a scale of 0-100, where:
- 90-100: Perfect match, highly relevant
- 70-89: Good match, relevant
- 50-69: Moderate match, somewhat relevant
- 30-49: Weak match, loosely relevant
- 0-29: Poor match, not relevant

Return ONLY a number between 0-100.`;

      const response = await openaiService.generalQuery(
        prompt,
        'You are a keyword relevance analyst. Analyze and return ONLY a number 0-100.'
      );
      
      const score = parseInt(response.trim());
      return isNaN(score) ? 50 : Math.min(100, Math.max(0, score));
    } catch (error) {
      console.error('Error calculating relevance score:', error);
      return 50; // Default middle score on error
    }
  },
  
  /**
   * Generate mock keyword ideas for demo mode
   * Enhanced with proper German keywords when language is German
   */
  generateMockKeywordIdeas(seedKeywords: string[], language: string = 'English', analysis?: LandingPageAnalysis): KeywordIdea[] {
    // Detect if German based on explicit language parameter
    const isGerman = language === 'German' || language === 'Deutsch';
    
    console.log(`🎯 generateMockKeywordIdeas: Language=${language}, isGerman=${isGerman}`);
    
    // Get industry context for better keyword generation
    const industry = analysis?.industry?.toLowerCase() || '';
    
    // German-specific keyword templates by industry
    const germanIndustryKeywords: Record<string, string[]> = {
      'ai': ['KI Beratung', 'künstliche Intelligenz Unternehmen', 'KI Lösungen', 'KI Integration', 'KI Strategie', 'maschinelles Lernen Beratung', 'Automatisierung mit KI', 'KI für Mittelstand'],
      'consulting': ['Unternehmensberatung', 'Strategieberatung', 'Digitalberatung', 'IT Beratung', 'Managementberatung', 'Prozessoptimierung', 'Change Management Beratung'],
      'software': ['Software Entwicklung', 'Softwarelösungen', 'Anwendungsentwicklung', 'individuelle Software', 'Cloud Software', 'SaaS Lösungen', 'Unternehmenssoftware'],
      'technology': ['Technologieberatung', 'IT Lösungen', 'Digitale Transformation', 'Technologie Partner', 'IT Dienstleister', 'Tech Consulting'],
      'marketing': ['Online Marketing Agentur', 'Digitales Marketing', 'SEO Agentur', 'Performance Marketing', 'Social Media Marketing', 'Content Marketing'],
      'ecommerce': ['Online Shop erstellen', 'E-Commerce Lösungen', 'Webshop Entwicklung', 'Online Handel', 'E-Commerce Beratung'],
      'finance': ['Finanzberatung', 'Vermögensberatung', 'Finanzplanung', 'Anlageberatung', 'Finanzierungslösungen'],
      'real estate': ['Immobilien Beratung', 'Immobilienmakler', 'Immobilien kaufen', 'Hausverwaltung', 'Immobilien Investment'],
      'default': ['professionelle Beratung', 'Experten Lösungen', 'Dienstleistungen', 'Service Anbieter', 'Qualitätsservice']
    };
    
    // Find matching industry keywords
    let industryKeywords = germanIndustryKeywords['default'];
    for (const [key, keywords] of Object.entries(germanIndustryKeywords)) {
      if (industry.includes(key)) {
        industryKeywords = keywords;
        break;
      }
    }
    
    // German qualifiers and intent words
    const germanQualifiers = ['beste', 'professionelle', 'führende', 'erfahrene', 'zuverlässige', 'günstige'];
    const germanIntentWords = ['anfragen', 'buchen', 'kontaktieren', 'Angebot einholen', 'kostenlos testen'];
    const germanSuffixes = ['Anbieter', 'Dienstleister', 'Experten', 'Agentur', 'Partner', 'Service'];
    
    // English fallbacks
    const englishQualifiers = ['best', 'professional', 'leading', 'experienced', 'reliable', 'affordable'];
    
    const qualifiers = isGerman ? germanQualifiers : englishQualifiers;
    
    // Build keyword ideas based on language
    const mockIdeas: KeywordIdea[] = [];
    
    if (isGerman) {
      // Generate GERMAN keywords
      // 1. Industry-specific German keywords
      industryKeywords.forEach((kw, idx) => {
        mockIdeas.push({
          text: kw,
          monthlySearches: 5000 - (idx * 400),
          competition: idx < 3 ? 'HIGH' : 'MEDIUM',
          competitionIndex: 70 - (idx * 5),
          lowBidMicros: 3000000 + (idx * 200000),
          highBidMicros: 7000000 + (idx * 300000),
          avgBidMicros: 5000000 + (idx * 250000),
          lowBid: 3.00 + (idx * 0.2),
          highBid: 7.00 + (idx * 0.3),
          suggestedBid: 5.00 + (idx * 0.25),
          relevanceScore: 95 - (idx * 3),
          recommendedMatchType: 'PHRASE',
          matchTypeReason: 'Deutsche Branchenkeywords',
          matchTypeConfidence: 85
        });
      });
      
      // 2. Qualified German keywords (qualifier + base)
      qualifiers.slice(0, 3).forEach((qual, idx) => {
        const baseKw = industryKeywords[0] || 'Beratung';
        mockIdeas.push({
          text: `${qual} ${baseKw}`,
          monthlySearches: 2000 - (idx * 300),
          competition: 'MEDIUM',
          competitionIndex: 55 + (idx * 5),
          lowBidMicros: 2500000,
          highBidMicros: 5500000,
          avgBidMicros: 4000000,
          lowBid: 2.50,
          highBid: 5.50,
          suggestedBid: 4.00,
          relevanceScore: 85 - (idx * 5),
          recommendedMatchType: 'PHRASE',
          matchTypeReason: 'Qualifiziertes deutsches Keyword',
          matchTypeConfidence: 80
        });
      });
      
      // 3. German suffix variations
      germanSuffixes.slice(0, 3).forEach((suffix, idx) => {
        const baseWord = industry.includes('ai') ? 'KI' : (industry.includes('software') ? 'Software' : 'Beratung');
        mockIdeas.push({
          text: `${baseWord} ${suffix}`,
          monthlySearches: 1500 - (idx * 200),
          competition: 'LOW',
          competitionIndex: 40 + (idx * 5),
          lowBidMicros: 2000000,
          highBidMicros: 4500000,
          avgBidMicros: 3250000,
          lowBid: 2.00,
          highBid: 4.50,
          suggestedBid: 3.25,
          relevanceScore: 80 - (idx * 5),
          recommendedMatchType: 'BROAD',
          matchTypeReason: 'Deutsche Anbieter-Keywords',
          matchTypeConfidence: 75
        });
      });
      
    } else {
      // Generate ENGLISH keywords (existing logic improved)
      const englishKeywords = [
        seedKeywords[0] || 'consulting services',
        `${qualifiers[0]} ${seedKeywords[0] || 'software'}`,
        seedKeywords[1] || 'business solutions',
        `${seedKeywords[0] || 'service'} provider`,
        `${seedKeywords[0] || 'solution'} comparison`,
        'professional services',
        'enterprise solutions',
        'business consulting'
      ];
      
      englishKeywords.forEach((kw, idx) => {
        mockIdeas.push({
          text: kw,
          monthlySearches: 8000 - (idx * 700),
          competition: idx < 3 ? 'HIGH' : 'MEDIUM',
          competitionIndex: 65 - (idx * 4),
          lowBidMicros: 3500000,
          highBidMicros: 8000000,
          avgBidMicros: 5750000,
          lowBid: 3.50,
          highBid: 8.00,
          suggestedBid: 5.75,
          relevanceScore: 90 - (idx * 4),
          recommendedMatchType: 'PHRASE',
          matchTypeReason: 'Core English keyword',
          matchTypeConfidence: 80
        });
      });
    }
    
    // Add seed keyword variations (translated to German if needed)
    seedKeywords.slice(0, 3).forEach((seed, idx) => {
      const suffix = isGerman ? 'Anbieter' : 'provider';
      mockIdeas.push({
        text: `${seed} ${suffix}`,
        monthlySearches: 1200 - (idx * 200),
        competition: 'MEDIUM' as const,
        competitionIndex: 50 + (idx * 5),
        lowBidMicros: 2500000,
        highBidMicros: 5500000,
        avgBidMicros: 4000000,
        lowBid: 2.50,
        highBid: 5.50,
        suggestedBid: 4.00,
        relevanceScore: 75 - (idx * 5),
        recommendedMatchType: 'PHRASE' as const,
        matchTypeReason: isGerman ? 'Seed + deutsche Erweiterung' : 'Seed variation',
        matchTypeConfidence: 75
      });
    });
    
    const allIdeas = mockIdeas.slice(0, 20);
    
    console.log(`✅ Generated ${allIdeas.length} mock keywords in ${language}`);
    
    // Apply brand detection if analysis is provided
    if (analysis) {
      return allIdeas.map(idea => {
        const brandDetection = this.detectBrandKeyword(idea.text, analysis);
        return {
          ...idea,
          isBranded: brandDetection.isBranded,
          brandMatchType: brandDetection.matchType
        };
      });
    }
    
    return allIdeas;
  },

  /**
   * Generate keywords using Gemini based on scraped content
   * Augmented with RAG (Retrieval Augmented Generation) for industry best practices
   * 
   * @param analysis - Landing page analysis object
   * @param count - Number of keywords to generate (default: 20)
   * @param orgId - Organization ID for RAG context
   * @param targetLanguage - EXPLICIT target language (overrides analysis.detectedLanguageName)
   */
  async generateKeywordsWithGemini(
    analysis: any,
    count: number = 20,
    orgId: string = 'default',
    targetLanguage?: string
  ): Promise<KeywordIdea[]> {
    try {
      const client = getClient();
      
      // Use explicit targetLanguage if provided, otherwise fall back to detected language
      const language = targetLanguage || analysis.detectedLanguageName || 'German';
      const isGerman = language === 'German';
      
      console.log(`🌍 Keyword generation language: ${language} (explicit: ${targetLanguage ? 'yes' : 'no'})`);

      // RAG: Query relevant context from Knowledge Base and Static Templates
      console.log(`🔍 RAG: Fetching proven keywords for ${analysis.industry}...`);
      const provenKeywords = await ragService.queryRelevantKeywords(orgId, analysis.industry, 15);
      const negativeBlueprints = ragKnowledgeService.getNegativeKeywords(analysis.industry);
      
      const prompt = `Generate Google Ads keyword ideas based on this REAL website analysis and proven industry context.

WEBSITE ANALYSIS:
- Title: ${analysis.title}
- Industry: ${analysis.industry}
- Value Proposition: ${analysis.valueProposition}
- Target Audience: ${analysis.targetAudience}
- Business Name: ${analysis.businessName || 'Unknown'}
- Offerings: ${analysis.features?.join(', ') || analysis.keyBenefits?.join(', ') || 'Not specified'}

### INDUSTRY PROVEN CONTEXT (RAG):
Use these proven keyword structures and concepts if they match the business:
${provenKeywords.length > 0 ? provenKeywords.map(k => `• ${k}`).join('\n') : '• General industry best practices'}

NEGATIVE KEYWORDS TO CONSIDER:
${negativeBlueprints.length > 0 ? negativeBlueprints.map(k => `• ${k}`).join('\n') : '• General industry negatives'}

=== CRITICAL: TARGET LANGUAGE ===
TARGET LANGUAGE: ${language}
${isGerman ? `
🇩🇪 GERMAN KEYWORDS ONLY:
Generiere ALLE ${count} Keywords AUSSCHLIESSLICH auf DEUTSCH!
- KEINE englischen Keywords erlaubt (auch keine englischen Fachbegriffe wie "AI" - nutze "KI" stattdessen)
- Nutze deutsche Suchbegriffe, die echte deutsche Nutzer bei Google eingeben würden
- Beispiele: "KI Beratung", "künstliche Intelligenz Unternehmen", "Digitalisierung Mittelstand"
- VERBOTEN: Englische Begriffe wie "enterprise", "solutions", "consulting", "implementation"
` : `
🌍 ${language.toUpperCase()} KEYWORDS ONLY:
Generate ALL ${count} keywords EXCLUSIVELY in ${language}!
- Keywords must be in ${language} as real users would search on Google
- Translate any industry terms to ${language}
`}

GOOGLE ADS API v22 REQUIREMENTS:
- Integrate RAG context where applicable
- Relevant to industry: ${analysis.industry}
- Include transactional intent keywords
- Realistic search volumes and bids

Generate ${count} keyword ideas. Return ONLY JSON array:
[
  {
    "text": "keyword",
    "monthlySearches": 500-15000,
    "competition": "LOW|MEDIUM|HIGH",
    "competitionIndex": 0-100,
    "suggestedBid": 1.5-8.5,
    "relevanceScore": 60-100,
    "recommendedMatchType": "EXACT|PHRASE|BROAD",
    "matchTypeReason": "explanation",
    "intent": "transactional|informational"
  }
]`;

      console.log(`🤖 Generating ${count} keywords with RAG-augmented Gemini...`);

      const response = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Gemini 3 Flash - latest model
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.4,
          maxOutputTokens: 3000,
          responseMimeType: "application/json"
        }
      });

      const responseText = response.text || response.candidates?.[0]?.content?.parts?.[0]?.text || '[]';
      const cleanedText = responseText.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      const keywords = JSON.parse(cleanedText);

      // Convert to KeywordIdea format with proper types
      const keywordIdeas: KeywordIdea[] = keywords.map((kw: any) => ({
        text: kw.text,
        monthlySearches: kw.monthlySearches || 1000,
        competition: kw.competition || 'MEDIUM',
        competitionIndex: kw.competitionIndex || 50,
        lowBidMicros: Math.round((kw.suggestedBid || 3.0) * 0.7 * 1000000),
        highBidMicros: Math.round((kw.suggestedBid || 3.0) * 1.3 * 1000000),
        avgBidMicros: Math.round((kw.suggestedBid || 3.0) * 1000000),
        lowBid: (kw.suggestedBid || 3.0) * 0.7,
        highBid: (kw.suggestedBid || 3.0) * 1.3,
        suggestedBid: kw.suggestedBid || 3.0,
        relevanceScore: kw.relevanceScore || 80,
        recommendedMatchType: kw.recommendedMatchType || 'PHRASE',
        matchTypeReason: kw.matchTypeReason || 'Generated from analysis',
        matchTypeConfidence: kw.relevanceScore || 80
      }));

      console.log(`✅ Gemini generated ${keywordIdeas.length} RAG-augmented keywords for ${analysis.industry}`);
      
      return keywordIdeas;
      
    } catch (error) {
      console.error('❌ Gemini RAG keyword generation failed:', error);
      throw new Error(`Failed to generate keywords with Gemini: ${(error as Error).message}`);
    }
  },

  /**
   * Generate realistic search terms from actual keywords
   * Based on real keyword list, not hardcoded data
   */
  generateSearchTermsFromKeywords(keywords: any[], analysis: any): any[] {
    const language = analysis?.detectedLanguageName || 'German';
    const isGerman = language === 'German';
    
    console.log(`📊 Generating search terms from ${keywords.length} real keywords...`);
    
    // Generate variations
    const searchTerms: any[] = [];
    
    keywords.slice(0, 10).forEach(kw => {
      const keywordText = typeof kw === 'string' ? kw : kw.text;
      
      // Base keyword (exact match)
      searchTerms.push({
        query: keywordText,
        matchedKeyword: keywordText,
        impressions: Math.floor(Math.random() * 2000) + 500,
        clicks: Math.floor(Math.random() * 100) + 10,
        conversions: Math.floor(Math.random() * 10),
        cost: Math.random() * 300 + 50,
        ctr: 0,
        conversionRate: 0
      });
      
      // Add natural variations based on language
      const variations = isGerman
        ? [`${keywordText} kostenlos`, `beste ${keywordText}`, `${keywordText} vergleich`]
        : [`${keywordText} free`, `best ${keywordText}`, `${keywordText} comparison`];
      
      // Add 1-2 variations per keyword
      variations.slice(0, 2).forEach(variation => {
        searchTerms.push({
          query: variation,
          matchedKeyword: keywordText,
          impressions: Math.floor(Math.random() * 1000) + 100,
          clicks: Math.floor(Math.random() * 50) + 5,
          conversions: Math.floor(Math.random() * 5),
          cost: Math.random() * 150 + 25,
          ctr: 0,
          conversionRate: 0
        });
      });
    });
    
    // Calculate CTR and conversion rates
    const searchTermsWithMetrics = searchTerms.map(st => ({
      ...st,
      ctr: st.impressions > 0 ? (st.clicks / st.impressions) * 100 : 0,
      conversionRate: st.clicks > 0 ? (st.conversions / st.clicks) * 100 : 0
    }));
    
    console.log(`✅ Generated ${searchTermsWithMetrics.length} search terms from real keywords`);
    
    return searchTermsWithMetrics.slice(0, 30); // Top 30
  }
};

